/**
 * Transcription API client.
 * Uses local backend when VITE_API_URL is not set (proxy in dev, same-origin in prod).
 *
 * All error messages returned to UI are sanitized — no internal details leak.
 */
const API_BASE = import.meta.env.VITE_API_URL ?? "";

export type TranscribeJobStatus = "pending" | "processing" | "completed" | "error";

export type TranscribeJob = {
  status: TranscribeJobStatus;
  progress: string;
  progressStage?: string | null;
  progressPercent?: number | null;
  result?: {
    title: string;
    summary: string;
    midiUrl: string;
    plotUrl: string;
    musicXmlUrl?: string | null;
  };
  error?: string;
};

export type ApiResult = {
  title: string;
  summary: string;
  midiUrl?: string;
  audioUrl?: string;
  plotUrl?: string;
  musicXmlUrl?: string | null;
};

// ---------------------------------------------------------------------------
// Safe error handling — never expose raw server messages to the user
// ---------------------------------------------------------------------------
const SAFE_PREFIXES = [
  "No notes were detected",
  "Only WAV or MP3 files are supported",
  "File too large",
  "Server is at capacity",
  "The transcription service is temporarily unavailable",
  "Please try a different audio file",
];

const GENERIC_ERROR =
  "Something went wrong. Please try again or use a different recording.";

const NETWORK_ERROR =
  "Unable to reach the server. Please check your connection or try again shortly.";

const TIMEOUT_ERROR =
  "The transcription is taking longer than expected. Please try again with a shorter recording.";

function sanitizeError(raw: string | undefined | null): string {
  if (!raw) return GENERIC_ERROR;
  for (const prefix of SAFE_PREFIXES) {
    if (raw.includes(prefix)) return raw;
  }
  return GENERIC_ERROR;
}

function isNetworkError(err: unknown): boolean {
  return (
    err instanceof TypeError &&
    (err.message === "Failed to fetch" || err.message.includes("NetworkError"))
  );
}

// ---------------------------------------------------------------------------
// HTTP helpers
// ---------------------------------------------------------------------------
async function fetchWithApiKey(url: string, init?: RequestInit): Promise<Response> {
  const apiKey = import.meta.env.VITE_API_KEY;
  const headers = new Headers(init?.headers);
  if (apiKey) {
    headers.set("X-API-Key", apiKey);
  }
  return fetch(url, { ...init, headers });
}

export type TranscriptionParams = {
  onsetThreshold?: number;
  offsetThreshold?: number;
  frameThreshold?: number;
};

// ---------------------------------------------------------------------------
// API methods
// ---------------------------------------------------------------------------
export async function createTranscribeJob(
  file: File,
  params?: TranscriptionParams
): Promise<{ jobId: string }> {
  const formData = new FormData();
  formData.append("file", file);
  if (params?.onsetThreshold != null) {
    formData.append("onset_threshold", String(params.onsetThreshold));
  }
  if (params?.offsetThreshold != null) {
    formData.append("offset_threshold", String(params.offsetThreshold));
  }
  if (params?.frameThreshold != null) {
    formData.append("frame_threshold", String(params.frameThreshold));
  }

  let res: Response;
  try {
    res = await fetchWithApiKey(`${API_BASE}/api/transcribe`, {
      method: "POST",
      body: formData,
    });
  } catch (err) {
    if (isNetworkError(err)) throw new Error(NETWORK_ERROR);
    throw new Error(GENERIC_ERROR);
  }

  if (!res.ok) {
    // Try to extract a message from the JSON body but never expose raw text
    try {
      const body = await res.json();
      throw new Error(sanitizeError(body?.detail ?? body?.error));
    } catch (jsonErr) {
      if (jsonErr instanceof Error && jsonErr.message !== GENERIC_ERROR && SAFE_PREFIXES.some(p => jsonErr.message.includes(p))) {
        throw jsonErr;
      }
      if (res.status === 429) throw new Error("Server is at capacity. Please try again shortly.");
      throw new Error(GENERIC_ERROR);
    }
  }

  const data = await res.json();
  return { jobId: data.jobId };
}

export async function getJobStatus(jobId: string): Promise<TranscribeJob> {
  let res: Response;
  try {
    res = await fetchWithApiKey(`${API_BASE}/api/jobs/${jobId}`);
  } catch (err) {
    if (isNetworkError(err)) throw new Error(NETWORK_ERROR);
    throw new Error(GENERIC_ERROR);
  }

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(GENERIC_ERROR);
  }

  // Sanitize the error field before returning to the UI
  if (body.error) {
    body.error = sanitizeError(body.error);
  }

  return body as TranscribeJob;
}

export async function transcribeWithProgress(
  file: File,
  onStatus: (message: string, meta?: { stage?: string | null; percent?: number | null }) => void,
  params?: TranscriptionParams
): Promise<ApiResult> {
  onStatus("Uploading...");
  const { jobId } = await createTranscribeJob(file, params);

  const pollInterval = 800;
  const maxAttempts = 600; // ~8 minutes

  let attempts = 0;
  let consecutiveFailures = 0;
  const MAX_CONSECUTIVE_FAILURES = 5;

  while (attempts < maxAttempts) {
    let job: TranscribeJob;
    try {
      job = await getJobStatus(jobId);
      consecutiveFailures = 0; // reset on success
    } catch {
      consecutiveFailures++;
      if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
        throw new Error(NETWORK_ERROR);
      }
      // Transient failure — wait and retry
      await new Promise((r) => setTimeout(r, pollInterval * 2));
      attempts++;
      continue;
    }

    onStatus(job.progress, { stage: job.progressStage, percent: job.progressPercent });

    if (job.status === "completed" && job.result) {
      return {
        title: job.result.title,
        summary: job.result.summary,
        midiUrl: job.result.midiUrl,
        plotUrl: job.result.plotUrl,
        musicXmlUrl: job.result.musicXmlUrl ?? null,
      };
    }

    if (job.status === "error") {
      throw new Error(job.error ?? GENERIC_ERROR);
    }

    await new Promise((r) => setTimeout(r, pollInterval));
    attempts++;
  }

  throw new Error(TIMEOUT_ERROR);
}

export async function checkBackendHealth(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/api/health`, { method: "GET" });
    return res.ok;
  } catch {
    return false;
  }
}
